import React, {
  useCallback,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import styles from './exportedDocumentListPage.module.css';
import usePageTitle from '../../hooks/title.hook';
import LoadingOverlay from '../../components/LoadingOverlay';
import { useDownloadFile } from '../../hooks/file.hook';
import { useDownloadSearchResultDocuments, useGetSearchResultFileDetail } from '../../hooks/api/document.hook';
import { CloseAlertModal, closeModalInitialState } from '../../components/AlertModal';
import Button from '../../components/Button/button';
import CustomFrame from '../../components/CustomFrame/customFrame';
import { ApiError } from '../../services/http';
import useEffectOnce from '../../hooks/useEffectOnce.hook';

function Component() {
  usePageTitle('文書リストダウンロード');

  // states
  const [closeAlertModal, setCloseAlertModal] = useState(closeModalInitialState);

  // hooks
  const downloadFile = useDownloadFile();
  const location = useLocation();

  // API's
  const getSearchResultFileDetail = useGetSearchResultFileDetail();
  const { request: downloadSearchResultDocuments, loading: downloadSearchResultFileLoading } = useDownloadSearchResultDocuments();

  // constants
  const query = new URLSearchParams(location.search);
  const idFromQuery = query.get('exportId');

  const fetchSearchResultFileDetail = useCallback(async (searchKey: string) => {
    try {
      await getSearchResultFileDetail.request(searchKey);
    } catch (e) {
      setCloseAlertModal({
        ...closeAlertModal,
        text: (e as ApiError).message,
        open: true,
        onCancel() {
          setCloseAlertModal({ ...closeAlertModal, open: false });
        },
      });
    }
  }, [getSearchResultFileDetail, setCloseAlertModal]);

  // methods
  const onDownloadClick = useCallback(async (searchKey: string) => {
    try {
      const searchResultFile = await downloadSearchResultDocuments(searchKey);
      downloadFile(searchResultFile);
    } catch (e) {
      setCloseAlertModal({
        ...closeAlertModal,
        text: (e as ApiError).message,
        open: true,
        onCancel() {
          setCloseAlertModal({ ...closeAlertModal, open: false });
        },
      });
    }
  }, [closeAlertModal, downloadFile, downloadSearchResultDocuments]);

  // effects
  useEffectOnce(() => {
    if (!idFromQuery) {
      setCloseAlertModal({
        ...closeAlertModal,
        text: 'ダウンロードURLが無効です。',
        open: true,
        onCancel() {
          setCloseAlertModal({ ...closeAlertModal, open: false });
        },
      });
      return;
    }
    fetchSearchResultFileDetail(idFromQuery);
  });

  return (
    <>
      {
        getSearchResultFileDetail.data && (
          <CustomFrame
            title={(
              <div className={styles.titleContainer}>
                <p>ダウンロードの準備が完了しました。</p>
                <p>下記のダウンロードボタンからファイルをダウンロードしてください。</p>
              </div>
            )}
            desc={(
              <div className={styles.descContainer}>
                <p>
                  処理番号：
                  {getSearchResultFileDetail.data.searchResultNumber}
                </p>
                <p>
                  リクエスト日時：
                  {getSearchResultFileDetail.data.requestDatetime}
                </p>
                <p>
                  実行ユーザー：
                  {getSearchResultFileDetail.data.requestUser}
                </p>
                <p>
                  ダウンロード期限：
                  {getSearchResultFileDetail.data.accessLimitDatetime}
                </p>
                <p className={styles.fileName}>
                  ファイル名：
                  {getSearchResultFileDetail.data.fileName}
                </p>
                <p>
                  ファイルサイズ：
                  {getSearchResultFileDetail.data.fileSize}
                </p>
              </div>
          )}
            body={<Button content="ダウンロード" onClick={() => onDownloadClick(idFromQuery as string)} />}
          />
        )
      }
      {(getSearchResultFileDetail.loading || downloadSearchResultFileLoading) ? <LoadingOverlay /> : null}
      <CloseAlertModal open={closeAlertModal.open} text={closeAlertModal.text} onCancel={closeAlertModal.onCancel} closeButtonText="OK" />
    </>
  );
}

export default Component;
